import { useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLeads } from '@/hooks/useLeads';
import { HeroNumber } from '@/components/HeroNumber';

const PIPELINE_STAGES = [
  'akkoord', 'getekend', 'aanbetaling_gevraagd', 'aanbetaling_ontvangen',
  'werkbezoek_gepland', 'werkbezoek_gedaan', 'materiaal_besteld',
  'levering_gepland', 'geleverd'
];

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      fontSize: '11px', fontWeight: 500, textTransform: 'uppercase',
      letterSpacing: '0.07em', color: 'var(--text-muted)', marginBottom: '10px',
    }}>
      {children}
    </div>
  );
}

export default function PartnerPerformancePage() {
  const { effectivePartnerId } = useAuth();
  const { leads } = useLeads(effectivePartnerId);

  const stats = useMemo(() => {
    const total = leads.length;
    const contacted = leads.filter(l => l.status !== 'new').length;
    const quoted = leads.filter(l =>
      ['offerte_verstuurd', 'offerte_herzien', ...PIPELINE_STAGES, 'afgerond'].includes(l.status)
    ).length;
    const signed = leads.filter(l =>
      [...PIPELINE_STAGES, 'afgerond'].includes(l.status)
    ).length;

    const leadsWithValue = leads.filter(l => (l.estimated_max_eur ?? 0) > 0);
    const gemiddeld = leadsWithValue.length > 0
      ? leadsWithValue.reduce((s, l) => s + (l.estimated_max_eur || 0), 0) / leadsWithValue.length
      : 0;

    const pipelineWaarde = leads
      .filter(l => PIPELINE_STAGES.includes(l.status))
      .reduce((s, l) => s + (l.estimated_max_eur || 0), 0);

    const contactRatio = total > 0 ? Math.round((contacted / total) * 100) : 0;
    const convRatio    = total > 0 ? Math.round((signed   / total) * 100) : 0;

    return { total, contacted, quoted, signed, contactRatio, convRatio, gemiddeld, pipelineWaarde };
  }, [leads]);

  const funnelStages = [
    { label: 'Ontvangen',     count: stats.total,     color: 'var(--border-hover)' },
    { label: 'Gecontacteerd', count: stats.contacted, color: 'var(--accent)' },
    { label: 'Offerte',       count: stats.quoted,    color: '#6B9BB8' },
    { label: 'Akkoord',       count: stats.signed,    color: 'var(--success-bright)' },
  ];
  const maxCount = funnelStages[0].count || 1;

  return (
    <div className="space-y-8 max-w-4xl mx-auto animate-fade-up">

      {/* Header */}
      <div className="stagger-1">
        <h1 className="text-xl font-semibold" style={{ color: 'var(--text-primary)' }}>
          Performance
        </h1>
        <p className="text-sm mt-1" style={{ color: 'var(--text-tertiary)' }}>
          Overzicht van jouw pipeline
        </p>
      </div>

      {/* Hero metric — leads received, the number that anchors the page */}
      <div className="stagger-2" style={{
        padding: '24px',
        background: 'var(--bg-surface-raised)',
        border: '1px solid var(--border-separator)',
        borderRadius: '16px',
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr',
        gap: '32px',
      }}>
        <HeroNumber
          label="Leads ontvangen"
          value={stats.total.toLocaleString('nl-NL')}
          sub="Totaal binnengekomen"
          size="xl"
        />
        <HeroNumber
          label="Contactratio"
          value={`${stats.contactRatio}%`}
          sub={`${stats.contacted} van ${stats.total} leads`}
          size="xl"
        />
        <HeroNumber
          label="Conversieratio"
          value={`${stats.convRatio}%`}
          sub={`${stats.signed} akkoord`}
          size="xl"
        />
      </div>

      {/* Funnel with drop-off labels */}
      <div className="stagger-3">
        <SectionLabel>Conversietrechter</SectionLabel>
        <div style={{
          background: 'var(--bg-surface-raised)',
          border: '1px solid var(--border-separator)',
          borderRadius: '12px',
          padding: '16px 20px',
        }}>
          {funnelStages.map((stage, i) => {
            const pct = Math.round((stage.count / maxCount) * 100);
            const prevCount = i > 0 ? funnelStages[i - 1].count : null;
            const dropOff = prevCount && prevCount > 0
              ? Math.round(((prevCount - stage.count) / prevCount) * 100)
              : null;

            return (
              <div key={stage.label}>
                {dropOff !== null && dropOff > 0 && (
                  <div style={{
                    fontSize: '11px', color: 'var(--text-muted)',
                    padding: '3px 0 3px 100px', fontStyle: 'italic',
                  }}>
                    −{dropOff}% hier
                  </div>
                )}
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '12px',
                  marginBottom: i < funnelStages.length - 1 ? '6px' : 0,
                }}>
                  <span style={{
                    fontSize: '13px', width: '92px', flexShrink: 0,
                    color: 'var(--text-secondary)',
                  }}>
                    {stage.label}
                  </span>
                  <div style={{
                    flex: 1, height: '24px', background: 'var(--bg-input)',
                    borderRadius: '6px', overflow: 'hidden',
                  }}>
                    <div style={{
                      width: `${Math.max(pct, stage.count > 0 ? 2 : 0)}%`,
                      height: '100%', background: stage.color, borderRadius: '6px',
                      display: 'flex', alignItems: 'center', paddingLeft: '8px',
                      transition: 'width 0.7s ease',
                    }}>
                      {stage.count > 0 && (
                        <span style={{ fontSize: '11px', fontWeight: 500, color: '#fff' }}>
                          {stage.count}
                        </span>
                      )}
                    </div>
                  </div>
                  <span style={{
                    fontSize: '13px', width: '36px', textAlign: 'right',
                    flexShrink: 0,
                    color: i === funnelStages.length - 1 && stage.count > 0
                      ? 'var(--success-bright)' : 'var(--text-muted)',
                    fontWeight: i === funnelStages.length - 1 && stage.count > 0 ? 500 : 400,
                  }}>
                    {pct}%
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Revenue metrics */}
      <div className="stagger-4">
        <SectionLabel>Omzetindicatie</SectionLabel>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
          {/* Gemiddelde — hero treatment */}
          <div style={{
            padding: '20px 24px',
            background: 'var(--accent-dim)',
            border: '1px solid var(--accent)',
            borderRadius: '12px',
          }}>
            <HeroNumber
              label="Gemiddelde opdracht"
              value={stats.gemiddeld > 0
                ? `€${Math.round(stats.gemiddeld).toLocaleString('nl-NL')}`
                : '—'}
              sub="Schatting op basis van m²"
              size="lg"
              accent
            />
          </div>
          {/* Pipeline */}
          <div style={{
            padding: '20px 24px',
            background: 'var(--bg-surface-raised)',
            border: '1px solid var(--border-separator)',
            borderRadius: '12px',
          }}>
            <HeroNumber
              label="Pipeline waarde"
              value={stats.pipelineWaarde > 0
                ? `€${Math.round(stats.pipelineWaarde).toLocaleString('nl-NL')}`
                : '—'}
              sub="Actieve leads met waarde"
              size="lg"
            />
          </div>
        </div>
      </div>

    </div>
  );
}
